import * as React from 'react';
import { LockOutlined, UserOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Draft, Progress, Stack, Text } from '@resi-media/resi-ui';
import { useSetAtom } from 'jotai';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { Panel } from '@studio/components/Panel';
import type { Credentials } from '@studio/store';
import { loginAtom } from '@preview/store/auth';

type _Props = {
  error: Error | null;
  isLoggingIn: boolean;
  sessionExpired: boolean;
};

const LoginForm = ({ error, isLoggingIn, sessionExpired }: _Props) => {
  const login = useSetAtom(loginAtom);
  const initialValues: Credentials = { username: '', password: '' };
  const [type, setType] = React.useState<'password' | 'text'>('password');
  const [domainError, setDomainError] = React.useState<boolean>(false);

  const LoginSchema = Yup.object({
    username: Yup.string().required('Username or Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm<Credentials>({
    mode: 'all',
    defaultValues: initialValues,
    resolver: yupResolver(LoginSchema),
  });

  const {
    // Read the formState before render to subscribe the form state through the Proxy
    formState: { dirtyFields: _Df, isDirty: isFormDirty, isValid: isFormValid, touchedFields: _Tf },
  } = methods;

  const onSubmit = (credentials: Credentials) => {
    // Clear any previous domain error
    setDomainError(false);

    // Check if username ends with @resi.io
    if (!credentials.username.endsWith('@resi.io')) {
      setDomainError(true);
      return;
    }

    login(credentials);
  };

  const toggleViewPassword = () => {
    setType(type === 'password' ? 'text' : 'password');
  };

  return (
    <FormProvider {...methods}>
      <Panel maxWidth="464px">
        <form data-testid="login-form" onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="xl">
            <Stack alignItems="center">
              {sessionExpired ? (
                <>
                  <Text as="h4" dataTestId="session-expired-text">
                    Your session has&#32;
                    <Text colorVariant="warning" isInline variant="h4">
                      expired
                    </Text>
                  </Text>
                  <Text variant="body3">Please sign in again to continue.</Text>
                </>
              ) : (
                <>
                  <Text as="h4" data-testid="title">
                    Welcome Back!
                  </Text>
                  <Text variant="body3">We're so glad to have you.</Text>
                </>
              )}
            </Stack>
            {(error || domainError) && (
              <Draft.AlertBanner>
                Login failed. Please check your username and password. If needed, you can reset your password by
                clicking the "Forgot Password" link below.
              </Draft.AlertBanner>
            )}
            <Stack gap="m">
              <Controller
                name="username"
                render={({ field: { name, onBlur, onChange, value }, fieldState: { error, isTouched } }) => {
                  return (
                    <Draft.FormField
                      error={error?.message}
                      fieldLabel="Username or Email"
                      htmlFor="username"
                      touched={isTouched}
                    >
                      <Draft.TextInput
                        autoComplete="username"
                        data-testid="username"
                        hasError={Boolean(error?.message)}
                        id="username"
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        startNode={<UserOutlined />}
                        value={value}
                      />
                    </Draft.FormField>
                  );
                }}
              />
              <Controller
                name="password"
                render={({ field: { name, onBlur, onChange, value }, fieldState: { error, isTouched } }) => {
                  return (
                    <Draft.FormField error={error?.message} fieldLabel="Password" htmlFor={name} touched={isTouched}>
                      <Draft.TextInput<'input'>
                        autoComplete="current-password"
                        data-testid="password"
                        endNode={
                          type === 'password' ? (
                            <EyeOutlined data-testid="eye-outlined" onClick={toggleViewPassword} />
                          ) : (
                            <EyeInvisibleOutlined data-testid="eye-invisible" onClick={toggleViewPassword} />
                          )
                        }
                        hasError={Boolean(error?.message)}
                        id={name}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        startNode={<LockOutlined />}
                        type={type}
                        value={value}
                      />
                    </Draft.FormField>
                  );
                }}
              />
            </Stack>
            <Stack gap="m">
              <Draft.Button
                data-testid="sign-in-button"
                isFullWidth
                {...(isLoggingIn && {
                  startNode: <Progress colorVariant="inherit" sizeVariant="inherit" />,
                })}
                disabled={isLoggingIn || !isFormDirty || !isFormValid}
                label="Sign In"
                type="submit"
              />
            </Stack>
          </Stack>
        </form>
      </Panel>
    </FormProvider>
  );
};

LoginForm.displayName = 'LoginForm';
export default LoginForm;
